import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { AppBaseQueryFn } from "../api";

/**
 * DRF list contract of the Integration Platform API (CAL-5972).
 *
 * Every list endpoint answers the PageNumberPagination envelope
 * `{count, next, previous, results}` produced by the backend's
 * `PagePagination` (backend/integration_platform/api/core/pagination.py):
 * default page size 20, `?page_size=` honoured up to MAX_PAGE_SIZE,
 * `?page=N` numbering, deterministic ordering.
 *
 * Pickers that must show EVERY row (providers, connections, credentials,
 * schedules, webhook triggers, config profiles) go through `fetchAllPages`.
 * Reading `results` of page 1 silently truncates the list - that is the bug
 * this module exists to make impossible. A single page is legitimate only
 * for paged tables (monitor, runs - they keep the envelope and drive
 * `page`/`page_size` themselves) and for server-search autocompletes
 * (clients, top 50 - `unwrapListResponse`).
 *
 * `next` is an absolute URL that DRF builds from the request host, which
 * differs behind API Gateway / CloudFront: it is used ONLY as a "has more"
 * flag and never followed. Pages are requested as `?page=N` against the
 * same relative url and params, so the auth header, `X-View-As-User` and
 * the token refresh of the shared base query apply to every page.
 */

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Single-page shape: a bare array (unpaginated APIView) or the DRF envelope.
export type ListResponse<T> = T[] | PaginatedResponse<T>;

// Mirrors PagePagination.max_page_size on the backend.
export const MAX_PAGE_SIZE = 200;

// Hard cap per list fetch (50 * 200 = 10k rows). Exceeding it is an error,
// not a truncated list: a picker with that many rows needs server search.
export const MAX_LIST_PAGES = 50;

// The 4th argument RTK passes to `queryFn`: the api's base query with `api`
// and `extraOptions` already bound. Derived from the real base query so the
// meta type stays whatever `api.tsx` declares.
export type FetchWithBaseQuery = (
  args: string | FetchArgs,
) => ReturnType<AppBaseQueryFn>;

// What a list `queryFn` resolves to - structurally RTK's
// QueryReturnValue<T[], FetchBaseQueryError>, which the public entry point of
// @reduxjs/toolkit/query 2.0 does not export. Both keys are declared on both
// members so callers can read `result.error` / `result.data` directly.
export type ListQueryResult<T> =
  | { data: T[]; error?: undefined }
  | { data?: undefined; error: FetchBaseQueryError };

export interface FetchAllPagesOptions {
  // Override of MAX_LIST_PAGES for callers that know their bound.
  maxPages?: number;
}

/** Rows of an intentionally single-page list query (server-search pickers). */
export function unwrapListResponse<T>(resp: ListResponse<T>): T[] {
  return Array.isArray(resp) ? resp : resp.results;
}

function isPaginatedResponse<T>(body: unknown): body is PaginatedResponse<T> {
  return (
    typeof body === "object" &&
    body !== null &&
    Array.isArray((body as { results?: unknown }).results)
  );
}

const listError = (message: string): FetchBaseQueryError => ({
  status: "CUSTOM_ERROR",
  error: message,
});

/**
 * GET every page of a DRF list endpoint and return the concatenated rows.
 *
 * `page` and `page_size` are appended to (and override) `params`; undefined
 * values in `params` are dropped by fetchBaseQuery. The first failing page's
 * error is returned unchanged. A bare array response is returned as-is - an
 * unpaginated view ignores `page`, so it can only ever be the first request.
 */
export async function fetchAllPages<T>(
  fetchWithBQ: FetchWithBaseQuery,
  url: string,
  params?: FetchArgs["params"],
  options: FetchAllPagesOptions = {},
): Promise<ListQueryResult<T>> {
  const maxPages = options.maxPages ?? MAX_LIST_PAGES;
  const rows: T[] = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const result = await fetchWithBQ({
      url,
      method: "GET",
      params: { ...params, page, page_size: MAX_PAGE_SIZE },
    });
    if (result.error) return { error: result.error };
    const body: unknown = result.data;
    if (Array.isArray(body)) return { data: body as T[] };
    if (!isPaginatedResponse<T>(body)) {
      return {
        error: listError(`Unexpected list response shape from ${url}.`),
      };
    }
    rows.push(...body.results);
    if (body.next == null) return { data: rows };
  }
  return {
    error: listError(
      `${url} has more than ${maxPages} pages of ${MAX_PAGE_SIZE} rows; ` +
        "refusing to return a truncated list.",
    ),
  };
}
